import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { USER_QUERY_KEYS } from './keys';

import * as userService from 'services/user';
import { fetchLeaveIssuerChangeRequestByUserId } from 'services/leaveIssuerChangeRequest';

import { handleError } from 'utils/errorHandler';

import type { ChangeTentativeExit } from 'interface/tentativeExit';
import type { AllocationItem } from 'interface/allocation';
import type { QueryObject } from 'interface/queryParam';
import type { ServiceId, ServiceParams, ServicePayload } from 'interface/service';

type UserListResponse = { data?: unknown[]; meta?: { total?: number } };

export const getUsersQueryKey = (params: ServiceParams = {}) => [USER_QUERY_KEYS.LIST, params] as const;

export const usersQueryOptions = (params: ServiceParams = {}) => ({
  queryKey: getUsersQueryKey(params),
  queryFn: () => userService.fetchAll(params) as Promise<UserListResponse>,
});

export const useFetchUsersQuery = (
  params: ServiceParams = {},
  enabled = true
): UseQueryResult<UserListResponse, Error> => {
  return useQuery({
    ...usersQueryOptions(params),
    enabled,
  });
};

export const getUserByIdQueryKey = (id?: ServiceId) => [USER_QUERY_KEYS.DETAIL, id] as const;

export const userByIdQueryOptions = (id: ServiceId) => ({
  queryKey: getUserByIdQueryKey(id),
  queryFn: () => userService.fetchById(id),
});

export const useFetchUserByIdQuery = <TData = unknown>(
  id?: ServiceId,
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getUserByIdQueryKey(id),
    queryFn: () => userService.fetchById(id!) as Promise<TData>,
    enabled: Boolean(id) && enabled,
  });
};

export const getUserByEmailQueryKey = (email: string) => [USER_QUERY_KEYS.LIST, 'email', email] as const;

export const useFetchUserByEmailQuery = <TData = unknown>(
  email: string,
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getUserByEmailQueryKey(email),
    queryFn: () => userService.fetchByEmail(email) as Promise<TData>,
    enabled: Boolean(email) && enabled,
  });
};

export const getUserCurrentAllocationsQueryKey = (userId?: ServiceId) =>
  [USER_QUERY_KEYS.CURRENT_ALLOCATIONS, userId] as const;

export const useFetchUserCurrentAllocationsQuery = <TData = AllocationItem[]>(
  userId?: ServiceId,
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getUserCurrentAllocationsQueryKey(userId),
    queryFn: () => userService.fetchUserCurrentAllocations(userId!) as Promise<TData>,
    enabled: Boolean(userId) && enabled,
  });
};

export const getUserBookingAllocationQueryKey = (userId?: ServiceId) =>
  [USER_QUERY_KEYS.BOOKING_ALLOCATION, userId] as const;

export const useFetchUserBookingAllocationQuery = <TData = unknown>(
  userId?: ServiceId,
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getUserBookingAllocationQueryKey(userId),
    queryFn: () => userService.fetchUserBookingAllocation(userId!) as Promise<TData>,
    enabled: Boolean(userId) && enabled,
  });
};

export const getUserWorkStatusQueryKey = (params: ServiceParams) => [USER_QUERY_KEYS.WORK_STATUS, params] as const;

export const useFetchUserWorkStatusQuery = <TData = unknown>(
  params: ServiceParams,
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getUserWorkStatusQueryKey(params),
    queryFn: () => userService.fetchUserWorkStatus(params) as Promise<TData>,
    enabled,
  });
};

export const getResignationQueryKey = (userId?: ServiceId) => [USER_QUERY_KEYS.RESIGNATIONS, userId] as const;

export const useFetchResignationQuery = <TData = unknown>(
  userId?: ServiceId,
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getResignationQueryKey(userId),
    queryFn: () => userService.fetchResignation(userId!) as Promise<TData>,
    enabled: Boolean(userId) && enabled,
  });
};

export const getLeaveIssuerHistoryQueryKey = (id?: ServiceId) => [USER_QUERY_KEYS.LEAVE_ISSUER_HISTORY, id] as const;

export const useFetchLeaveIssuerHistoryQuery = <TData = unknown[]>(
  id?: ServiceId,
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getLeaveIssuerHistoryQueryKey(id),
    queryFn: () => userService.fetchLeaveIssuerHistory(id!) as Promise<TData>,
    enabled: Boolean(id) && enabled,
  });
};

export const getLeaveIssuerChangeRequestQueryKey = (id?: ServiceId) =>
  [USER_QUERY_KEYS.LEAVE_ISSUER_CHANGE_REQUEST, id] as const;

export const useFetchLeaveIssuerChangeRequestQuery = <TData = unknown>(
  id?: ServiceId
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getLeaveIssuerChangeRequestQueryKey(id),
    queryFn: async () => ((await fetchLeaveIssuerChangeRequestByUserId(id!)) as { data: TData }).data,
    enabled: Boolean(id),
    retry: false,
  });
};

export const getTeamManagerHistoryQueryKey = (id?: ServiceId) => [USER_QUERY_KEYS.TEAM_MANAGER_HISTORY, id] as const;

export const useFetchTeamManagerHistoryQuery = <TData = unknown[]>(
  id?: ServiceId,
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getTeamManagerHistoryQueryKey(id),
    queryFn: () => userService.fetchTeamManagerHistory(id!) as Promise<TData>,
    enabled: Boolean(id) && enabled,
  });
};

export const getUserRolesQueryKey = (id?: ServiceId) => [USER_QUERY_KEYS.ROLES, id] as const;

export const useFetchUserRolesQuery = <TData = unknown>(
  id?: ServiceId,
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getUserRolesQueryKey(id),
    queryFn: () => userService.fetchRoles(id!) as Promise<TData>,
    enabled: Boolean(id) && enabled,
  });
};

export const getTeamMembersQueryKey = (id?: ServiceId, params: ServiceParams = {}) =>
  [USER_QUERY_KEYS.TEAM_MEMBERS, id, params] as const;

export const teamMembersQueryOptions = (id: ServiceId, params: ServiceParams = {}) => ({
  queryKey: getTeamMembersQueryKey(id, params),
  queryFn: () => userService.fetchTeamMembers(id, params),
});

export const useFetchTeamMembersQuery = <TData = unknown>(
  id?: ServiceId,
  params: ServiceParams = {},
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    ...teamMembersQueryOptions(id!, params),
    enabled: Boolean(id) && enabled,
  }) as UseQueryResult<TData, Error>;
};

export const getIssueeCountQueryKey = (id?: ServiceId, fetchType: ServiceParams = {}) =>
  [USER_QUERY_KEYS.ISSUEE_COUNT, id, fetchType] as const;

export const useFetchIssueeCountQuery = <TData = { count?: number }>(
  id?: ServiceId,
  fetchType: ServiceParams = {},
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getIssueeCountQueryKey(id, fetchType),
    queryFn: () => userService.fetchIssueeCount(id!, fetchType) as Promise<TData>,
    enabled: Boolean(id) && enabled,
  });
};

export const getPeopleFinderQueryKey = (params: ServiceParams = {}) => [USER_QUERY_KEYS.PEOPLE_FINDER, params] as const;

export const useFetchPeopleFinderQuery = <TData = unknown>(
  params: ServiceParams = {},
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getPeopleFinderQueryKey(params),
    queryFn: () => userService.fetchPeopleFinder(params) as Promise<TData>,
    enabled,
  });
};

export const getResourceQueryKey = (params: ServiceParams = {}) => [USER_QUERY_KEYS.RESOURCE, params] as const;

export const useFetchResourceQuery = <TData = unknown>(
  params: ServiceParams = {},
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getResourceQueryKey(params),
    queryFn: () => userService.fetchResource(params) as Promise<TData>,
    enabled,
  });
};

export const useExportPeopleFinderMutation = () => {
  return useMutation({
    mutationFn: (params: ServiceParams) => userService.exportPeopleFinder(params),
    onError: handleMutationError,
  });
};

export const useExportResourceMutation = () => {
  return useMutation({
    mutationFn: (params: ServiceParams) => userService.exportResource(params),
    onError: handleMutationError,
  });
};

const useInvalidateUserQueries = () => {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEYS.LIST] }),
    invalidateUser: (id: ServiceId) => queryClient.invalidateQueries({ queryKey: getUserByIdQueryKey(id) }),
    invalidateResignation: (userId: ServiceId) =>
      queryClient.invalidateQueries({ queryKey: getResignationQueryKey(userId) }),
  };
};

const handleMutationError = (error: object) => handleError(error);

export const useCreateUserMutation = () => {
  const { invalidateAll } = useInvalidateUserQueries();

  return useMutation({
    mutationFn: (user: ServicePayload) => userService.create(user),
    onSuccess: invalidateAll,
    onError: handleMutationError,
  });
};

export const useUpdateUserMutation = () => {
  const { invalidateAll, invalidateUser } = useInvalidateUserQueries();

  return useMutation({
    mutationFn: ({ user, id }: { user: ServicePayload; id: ServiceId }) => userService.update(user, id),
    onSuccess: (_data, { id }) => {
      invalidateAll();
      invalidateUser(id);
    },
    onError: handleMutationError,
  });
};

export const useUploadUserImageMutation = () => {
  const { invalidateUser } = useInvalidateUserQueries();

  return useMutation({
    mutationFn: ({ userId, formData }: { userId: ServiceId; formData: FormData }) =>
      userService.upload(userId, formData),
    onSuccess: (_data, { userId }) => invalidateUser(userId),
    onError: handleMutationError,
  });
};

export const useActivateUserMutation = () => {
  const { invalidateUser } = useInvalidateUserQueries();

  return useMutation({
    mutationFn: ({ id, payload }: { id: ServiceId; payload: ServicePayload }) => userService.activateUser(id, payload),
    onSuccess: (_data, { id }) => invalidateUser(id),
    onError: handleMutationError,
  });
};

export const useUpdateDesignationMutation = () => {
  const { invalidateUser } = useInvalidateUserQueries();

  return useMutation({
    mutationFn: ({ designation, id }: { designation: ServicePayload; id: ServiceId }) =>
      userService.updateDesignation(designation, id),
    onSuccess: (_data, { id }) => invalidateUser(id),
    onError: handleMutationError,
  });
};

export const useUpdateLeaveIssuerMutation = () => {
  const { invalidateUser } = useInvalidateUserQueries();

  return useMutation({
    mutationFn: ({ leaveIssuer, id }: { leaveIssuer: ServicePayload; id: ServiceId }) =>
      userService.updateLeaveIssuer(leaveIssuer, id),
    onSuccess: (_data, { id }) => {
      invalidateUser(id);
    },
    onError: handleMutationError,
  });
};

export const useUpdateTeamManagerMutation = () => {
  const { invalidateUser } = useInvalidateUserQueries();

  return useMutation({
    mutationFn: ({ teamManager, id }: { teamManager: ServicePayload; id: ServiceId }) =>
      userService.updateTeamManager(teamManager, id),
    onSuccess: (_data, { id }) => invalidateUser(id),
    onError: handleMutationError,
  });
};

export const useUpdateEngagementStatusMutation = () => {
  const { invalidateUser } = useInvalidateUserQueries();

  return useMutation({
    mutationFn: ({ id, payload }: { id: ServiceId; payload: ServicePayload }) =>
      userService.updateEngagementStatus(id, payload),
    onSuccess: (_data, { id }) => invalidateUser(id),
    onError: handleMutationError,
  });
};

export const useCreateResignationMutation = () => {
  const { invalidateUser, invalidateResignation } = useInvalidateUserQueries();

  return useMutation({
    mutationFn: ({ resignation, userId }: { resignation: ChangeTentativeExit; userId: ServiceId }) =>
      userService.createResignation(resignation, userId),
    onSuccess: (_data, { userId }) => {
      invalidateUser(userId);
      invalidateResignation(userId);
    },
    onError: handleMutationError,
  });
};

export const useUpdateResignationMutation = () => {
  const { invalidateUser, invalidateResignation } = useInvalidateUserQueries();

  return useMutation({
    mutationFn: ({ resignation, userId, id }: { resignation: ChangeTentativeExit; userId: ServiceId; id: ServiceId }) =>
      userService.updateResignation(resignation, userId, id),
    onSuccess: (_data, { userId }) => {
      invalidateUser(userId);
      invalidateResignation(userId);
    },
    onError: handleMutationError,
  });
};

export const useDeleteResignationMutation = () => {
  const { invalidateUser, invalidateResignation } = useInvalidateUserQueries();

  return useMutation({
    mutationFn: ({ userId, id }: { userId: ServiceId; id: ServiceId }) => userService.deleteResignation(userId, id),
    onSuccess: (_data, { userId }) => {
      invalidateUser(userId);
      invalidateResignation(userId);
    },
    onError: handleMutationError,
  });
};

export const useAddUserRoleMutation = () => {
  const { invalidateUser } = useInvalidateUserQueries();

  return useMutation({
    mutationFn: ({ id, roles }: { id: ServiceId; roles: ReadonlyArray<{ id: number; name?: string }> }) =>
      userService.addRole(id, roles),
    onSuccess: (_data, { id }) => invalidateUser(id),
    onError: handleMutationError,
  });
};

export const useApproveDirectSkillMutation = () => {
  const { invalidateUser } = useInvalidateUserQueries();

  return useMutation({
    mutationFn: ({ userId, skillId, payload }: { userId: ServiceId; skillId: ServiceId; payload: ServicePayload }) =>
      userService.approveDirectSkill(userId, skillId, payload),
    onSuccess: (_data, { userId }) => invalidateUser(userId),
    onError: handleMutationError,
  });
};

export type { QueryObject };
